import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, Trash2, ListTodo } from "lucide-react";
import { toast } from "sonner";

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tasks");
      setTasks(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Display order: active first (by position desc, matches API order),
  // done at the bottom. We keep `position` to drive backend reordering.
  const ordered = useMemo(() => {
    const active = tasks.filter((t) => !t.done);
    const done = tasks.filter((t) => t.done);
    return [...active, ...done];
  }, [tasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    try {
      const r = await api.post("/tasks", { text: t });
      setTasks((prev) => [r.data, ...prev]);
      setText("");
    } catch {
      toast.error("Nie udało się dodać zadania");
    }
  };

  const toggle = async (task) => {
    const next = !task.done;
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, done: next } : t))
    );
    try {
      await api.put(`/tasks/${task.id}`, { done: next });
    } catch {
      toast.error("Błąd aktualizacji");
      load();
    }
  };

  const remove = async (id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
    try {
      await api.delete(`/tasks/${id}`);
    } catch {
      toast.error("Błąd usuwania");
      load();
    }
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Reorder only within the active group (done tasks are pinned at bottom)
    const activeTasks = tasks.filter((t) => !t.done);
    const doneTasks = tasks.filter((t) => t.done);
    const oldIdx = activeTasks.findIndex((t) => t.id === active.id);
    const newIdx = activeTasks.findIndex((t) => t.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const moved = arrayMove(activeTasks, oldIdx, newIdx);
    const nextOrder = [...moved, ...doneTasks];
    setTasks(nextOrder);
    try {
      // Send full order so backend re-assigns positions
      await api.post("/tasks/reorder", { ids: nextOrder.map((t) => t.id) });
    } catch {
      toast.error("Błąd zmiany kolejności");
      load();
    }
  };

  const activeCount = tasks.filter((t) => !t.done).length;
  const doneCount = tasks.filter((t) => t.done).length;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-[var(--text-muted)] mb-2">
          Do zrobienia
        </div>
        <h1 className="heading text-3xl sm:text-4xl font-semibold tracking-tight">
          Lista zadań
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-2">
          {activeCount} aktywne · {doneCount} wykonane · przeciągnij ⠿ aby zmienić kolejność
        </p>
      </header>

      <div className="surface p-4 flex gap-2 items-end" data-testid="new-task-form">
        <div className="flex-1">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="np. Zamówić zawiasy do kuchni Nowak..."
            data-testid="new-task-input"
          />
        </div>
        <Button
          onClick={add}
          data-testid="add-task-btn"
          className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
        >
          <Plus size={16} className="mr-1" />
          Dodaj
        </Button>
      </div>

      {loading ? (
        <div className="surface p-10 text-center text-[var(--text-muted)]">Wczytywanie...</div>
      ) : tasks.length === 0 ? (
        <div className="surface p-12 text-center" data-testid="tasks-empty">
          <div className="w-14 h-14 rounded-full bg-[var(--surface-2)] mx-auto mb-4 flex items-center justify-center">
            <ListTodo className="text-[var(--primary)]" size={22} />
          </div>
          <div className="heading text-lg font-semibold">Brak zadań</div>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Dodaj pierwsze zadanie powyżej.
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={ordered.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="surface overflow-hidden" data-testid="tasks-list">
              {ordered.map((t, idx) => (
                <SortableTask
                  key={t.id}
                  task={t}
                  isLast={idx === ordered.length - 1}
                  onToggle={() => toggle(t)}
                  onRemove={() => remove(t.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function SortableTask({ task, isLast, onToggle, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: task.done });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? "var(--surface-2)" : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`task-${task.id}`}
      className={`flex items-center gap-3 px-4 py-3 ${
        !isLast ? "border-b border-[var(--border)]" : ""
      } ${task.done ? "bg-[var(--surface-2)]/40" : ""}`}
    >
      {!task.done ? (
        <button
          {...attributes}
          {...listeners}
          className="touch-none text-[var(--text-muted)] hover:text-[var(--text)] cursor-grab active:cursor-grabbing p-1"
          data-testid={`task-handle-${task.id}`}
          aria-label="Przeciągnij"
        >
          <GripVertical size={16} />
        </button>
      ) : (
        <div className="w-6 shrink-0" />
      )}
      <Checkbox
        checked={task.done}
        onCheckedChange={onToggle}
        data-testid={`task-check-${task.id}`}
      />
      <span
        className={`flex-1 text-sm ${
          task.done ? "line-through text-[var(--text-muted)]" : ""
        }`}
        data-testid={`task-text-${task.id}`}
      >
        {task.text}
      </span>
      <button
        onClick={onRemove}
        className="text-[var(--text-muted)] hover:text-[var(--danger)] transition-colors p-1"
        data-testid={`task-remove-${task.id}`}
        aria-label="Usuń zadanie"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}
